import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, CardCta, Divider, Heading, Paragraph } from '../Ant';
import CardData from '../dashboard/cardData';
import { ArrowRight, Briefcase, Calendar, CalendarDate, CircleTick, Next } from '../icons';
import { useTranslation } from 'react-i18next';
import {
  IPreliminaryInvoice,
  IPreliminaryInvoiceV2,
  MyInvoicingTab,
  ReviewStatus,
} from '../../interface/invoice';
import {
  formatter,
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import { usePreliminaryInvoiceActions } from '../../hooks/usePreliminaryInvoiceActions';
import NoteIcons from '../common/NoteIcons';
import { useSystemInfo } from '../../context/SystemInfoContext';
import {
  PreliminaryInvoiceStatusBorderColor,
  PreliminaryInvoiceStatusTagColor,
} from '../../constants/invoice';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const PreliminaryInvoiceGeneralCard = ({
  preliminaryInvoice,
  isLoading = false,
}: {
  preliminaryInvoice: IPreliminaryInvoice;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { push, goBack } = useNavigationStack();
  const { parameters } = useTransactionParameters();
  const baseCurrency = parameters?.currency;
  const { systemParams } = useSystemInfo();
  const isPreInvoiceV2 = systemParams?.featureFlags?.preliminaryInvoice?.version === 'v2';
  const handleGoToCustomer = () => {
    push(
      ROUTES.CUSTOMER_LISTING +
        `/${preliminaryInvoice?.company?.id ?? preliminaryInvoice?.privatePerson?.id}`,
    );
  };
  const navigateOnSuccess = () => {
    goBack({ fromTab: MyInvoicingTab.preliminaryInvoices });
  };
  const { onSkip, onReview, SkipModal, ReviewModal, ErrorModal } = usePreliminaryInvoiceActions({
    navigateOnSuccess,
  });
  const netAmountInMatterCurrency = isPreInvoiceV2
    ? ((preliminaryInvoice as unknown as IPreliminaryInvoiceV2)?.totalNetAmountInMatterCurrency ??
      0)
    : (preliminaryInvoice?.sumOfBillableEntriesInCurrency ?? 0);
  const netAmount = isPreInvoiceV2
    ? ((preliminaryInvoice as unknown as IPreliminaryInvoiceV2)?.totalNetAmount ?? 0)
    : (preliminaryInvoice?.sumOfBillableEntries ?? 0);

  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 1, 2, 2]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  if (!preliminaryInvoice) return null;
  return (
    <PrimaryCard
      data-testid="preliminary-invoice-general-card"
      $isDetail
      className={`d-flex flex-col gap-10 h-full conflict-card ${PreliminaryInvoiceStatusBorderColor[preliminaryInvoice?.status]}`}
    >
      <Flex align="center" className="w-full" gap={8}>
        <Flex
          justify="center"
          align="center"
          data-testid="preliminary-invoice-general-card-status-chip"
          className={`status-chip ${PreliminaryInvoiceStatusTagColor[preliminaryInvoice?.status]}`}
        >
          {t(`preliminaryInvoice:status.${preliminaryInvoice.status}`)}
        </Flex>
        <NoteIcons
          hasImportantNotes={preliminaryInvoice?.hasImportantNotes}
          hasImportantRelatedNotes={preliminaryInvoice?.hasImportantRelatedNotes}
          hasNotes={preliminaryInvoice?.hasNotes}
        />
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          type="matter"
          icon={
            <Briefcase
              className={getMatterIconClass(preliminaryInvoice?.matter?.invoiceableType ?? '')}
            />
          }
          title={getMatterLabel(t, preliminaryInvoice?.matter?.invoiceableType)}
          content={
            preliminaryInvoice?.company?.name ?? preliminaryInvoice?.privatePerson?.fullName ?? '-'
          }
          subcontent={
            preliminaryInvoice?.matter?.matterNumber &&
            `${preliminaryInvoice?.matter?.matterNumber} - ${preliminaryInvoice?.matter?.subject}`
          }
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-2"
          headingcontent={
            (preliminaryInvoice?.company?.id || preliminaryInvoice?.privatePerson?.id) && (
              <Button
                type="text"
                icon={<ArrowRight />}
                iconPosition="end"
                className="text-cta"
                data-testid="preliminary-invoice-general-card-view-button"
                onClick={handleGoToCustomer}
              >
                {getViewLabel(t, 'customer:customer')}
              </Button>
            )
          }
        />
        <CardData
          icon={<Calendar />}
          type="transaction"
          title={t('preliminaryInvoice:period')}
          content={`${getLocalDateAndTime(new Date(preliminaryInvoice?.periodStartDate), true)} - ${getLocalDateAndTime(new Date(preliminaryInvoice?.periodEndDate), true)}`}
        />
        <CardData
          icon={<CalendarDate />}
          type="entry-date"
          title={t('preliminaryInvoice:entryDate')}
          content={getLocalDateAndTime(new Date(preliminaryInvoice?.entryDate), true)}
        />
        <Divider />
        {baseCurrency && baseCurrency !== preliminaryInvoice?.matter?.currency && (
          <Flex justify="space-between">
            <Paragraph
              data-testid="preliminary-invoice-general-card-total-net-amount"
              $fontsize="13px"
              $weight={500}
              $color="title"
              $lineheight="20px"
            >
              {`${t('preliminaryInvoice:totalNetAmount')} (${preliminaryInvoice?.matter?.currency ?? 'EUR'})`}
            </Paragraph>
            <Heading
              data-testid="preliminary-invoice-general-card-total-net-amount-value"
              level={6}
              $fontsize="13px"
              $weight={700}
              $color="title"
              $lineheight="20px"
            >
              {formatter.regionalNumberFormat({
                value: netAmountInMatterCurrency,
                minimumFractionDigits: 2,
              })}
            </Heading>
          </Flex>
        )}
        <Flex justify="space-between">
          <Paragraph
            data-testid="preliminary-invoice-general-card-total-net-amount"
            $fontsize="13px"
            $weight={500}
            $color="title"
            $lineheight="20px"
          >
            {`${t('preliminaryInvoice:totalNetAmount')} (${baseCurrency ?? 'EUR'})`}
          </Paragraph>
          <Heading
            data-testid="preliminary-invoice-general-card-total-net-amount-value"
            level={6}
            $fontsize="13px"
            $weight={700}
            $color="title"
            $lineheight="20px"
          >
            {formatter.regionalNumberFormat({
              value: netAmount,
              minimumFractionDigits: 2,
            })}
          </Heading>
        </Flex>
      </Flex>
      {preliminaryInvoice?.reviewStatus === ReviewStatus.IN_REVIEW && (
        <Flex vertical gap={10}>
          <Divider />
          <Flex gap={10}>
            <CardCta
              data-testid="preliminary-invoice-general-card-mark-as-reviewed"
              onClick={() =>
                onReview({ id: preliminaryInvoice?.id, version: preliminaryInvoice?.version })
              }
            >
              <CircleTick />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="15px">
                {t('preliminaryInvoice:markAsReviewed')}
              </Paragraph>
            </CardCta>
            <CardCta
              data-testid="preliminary-invoice-general-card-skip-review"
              onClick={() =>
                onSkip({ id: preliminaryInvoice?.id, version: preliminaryInvoice?.version })
              }
            >
              <Next />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="15px">
                {t('preliminaryInvoice:skipReview')}
              </Paragraph>
            </CardCta>
          </Flex>
        </Flex>
      )}
      {ReviewModal}
      {SkipModal}
      {ErrorModal}
    </PrimaryCard>
  );
};

export default PreliminaryInvoiceGeneralCard;
